import React, { useEffect, useRef } from "react";
import { pickColor } from "./internal/colorUtils";

export const key = "rotating-half-circle-lines";

const DEFAULT_BG_COLOR = "#f8fafc";
const DEFAULT_LINE_COLOR = "#111827";
const LINE_COUNT = 29;
const ROTATION_SPEED = 0.00006;
const START_ANGLE = -78;
const END_ANGLE = 78;

const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

export default function RotatingHalfCircleLines({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef({
    background: DEFAULT_BG_COLOR,
    line: DEFAULT_LINE_COLOR,
  });

  useEffect(() => {
    colorsRef.current = {
      background: pickColor(colors, 0, DEFAULT_BG_COLOR),
      line: pickColor(colors, 1, DEFAULT_LINE_COLOR),
    };
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let startTime = null;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (timestamp) => {
      if (startTime === null) startTime = timestamp;

      const { background, line } = colorsRef.current;
      const rotation = (timestamp - startTime) * ROTATION_SPEED;
      const centerX = 0;
      const centerY = height * 0.5;
      const radius = Math.max(width, height) * 0.72;
      const innerRadius = radius * 0.32;
      const outerRadius = radius * 0.78;
      const startAngle = degreesToRadians(START_ANGLE);
      const endAngle = degreesToRadians(END_ANGLE);
      const angleStep = (endAngle - startAngle) / (LINE_COUNT - 1);
      const angleOffset = rotation % angleStep;

      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = line;
      ctx.lineCap = "butt";

      for (let i = -1; i <= LINE_COUNT; i += 1) {
        const angle = startAngle + i * angleStep + angleOffset;
        if (angle < startAngle || angle > endAngle) continue;

        const endY = centerY + Math.sin(angle) * outerRadius;
        const verticalPosition = Math.max(0, Math.min(1, endY / Math.max(1, height)));

        ctx.lineWidth = 0.8 + verticalPosition * 4.2;
        ctx.beginPath();
        ctx.moveTo(
          centerX + Math.cos(angle) * innerRadius,
          centerY + Math.sin(angle) * innerRadius
        );
        ctx.lineTo(
          centerX + Math.cos(angle) * outerRadius,
          centerY + Math.sin(angle) * outerRadius
        );
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
